import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Package, Plus, Truck, MapPin, Clock, AlertTriangle,
  CheckCircle, RefreshCw, Loader2, Bell, Filter,
  ArrowRight, Scale, Leaf, X, Eye
} from 'lucide-react';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Couleurs et icones par statut de benne
const BENNE_STYLES = {
  disponible: { color: '#9ca3af', icon: '⬜', label: 'Disponible', pulse: false },
  posee_parcelle: { color: '#3b82f6', icon: '📦', label: 'Posée', pulse: false },
  en_remplissage: { color: '#f59e0b', icon: '🔄', label: 'En remplissage', pulse: true },
  pleine: { color: '#22c55e', icon: '✅', label: 'PLEINE', pulse: true },
  en_transport: { color: '#8b5cf6', icon: '🚛', label: 'En transport', pulse: true },
  en_attente_distillation: { color: '#06b6d4', icon: '⏳', label: 'En attente', pulse: false },
  en_distillation: { color: '#ef4444', icon: '🔥', label: 'En distillation', pulse: true },
  distillee: { color: '#10b981', icon: '✨', label: 'Distillée', pulse: false },
  en_vidage: { color: '#78716c', icon: '🗑️', label: 'En vidage', pulse: false },
  a_nettoyer: { color: '#fbbf24', icon: '🧹', label: 'À nettoyer', pulse: false },
};

// Custom marker pour les bennes
const createBenneIcon = (benne) => {
  const style = BENNE_STYLES[benne.statut] || BENNE_STYLES.disponible;
  const bioClass = benne.est_bio ? 'ring-2 ring-green-500' : '';
  const pulseClass = style.pulse ? 'animate-pulse' : '';
  
  return L.divIcon({
    className: 'custom-benne-marker',
    html: `
      <div class="relative">
        <div class="w-10 h-10 rounded-lg flex items-center justify-center text-xl shadow-lg ${bioClass} ${pulseClass}" 
             style="background-color: ${style.color}">
          ${style.icon}
        </div>
        <div class="absolute -bottom-1 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-[10px] px-1.5 rounded font-bold whitespace-nowrap">
          ${benne.code}
        </div>
        ${benne.est_bio ? '<div class="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center text-[8px]">🌿</div>' : ''}
      </div>
    `,
    iconSize: [40, 50],
    iconAnchor: [20, 50],
    popupAnchor: [0, -50]
  });
};

// Composant légende
const BenneLegend = ({ stats }) => {
  return (
    <div className="absolute bottom-4 left-4 z-[1000] bg-background/95 backdrop-blur rounded-lg shadow-lg p-3 max-w-[200px]">
      <h4 className="font-semibold text-sm mb-2">Légende Bennes</h4>
      <div className="space-y-1">
        {Object.entries(BENNE_STYLES).map(([key, style]) => (
          <div key={key} className="flex items-center gap-2 text-xs">
            <div 
              className="w-5 h-5 rounded flex items-center justify-center"
              style={{ backgroundColor: style.color }}
            >
              {style.icon}
            </div>
            <span className="flex-1">{style.label}</span>
            {stats && stats[key] > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5">{stats[key]}</Badge>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Composant alertes
const AlertesPanel = ({ alertes, onTraiter, onRefresh }) => {
  if (!alertes || alertes.length === 0) return null;
  
  return (
    <div className="absolute top-4 right-4 z-[1000] w-80">
      <Card className="bg-background/95 backdrop-blur shadow-lg">
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Bell className="w-4 h-4 text-orange-500" />
              Alertes ({alertes.length})
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={onRefresh} className="h-7 w-7 p-0">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <ScrollArea className="max-h-[300px]">
            <div className="space-y-2">
              {alertes.slice(0, 5).map((alerte) => (
                <div 
                  key={alerte.id} 
                  className={`p-2 rounded-lg border text-xs ${
                    alerte.priorite === 'haute' || alerte.priorite === 'urgente' 
                      ? 'bg-red-50 border-red-200 dark:bg-red-900/20' 
                      : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-medium">{alerte.message}</p>
                      {alerte.producteur_nom && (
                        <p className="text-muted-foreground mt-1">
                          Producteur: {alerte.producteur_nom}
                        </p>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-6 w-6 p-0"
                      onClick={() => onTraiter(alerte.id)}
                    >
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

// Page principale
export default function BennesDistilleriePage() {
  const [bennes, setBennes] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [planning, setPlanning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [selectedBenne, setSelectedBenne] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSignalerPleineDialog, setShowSignalerPleineDialog] = useState(false);
  const [filterStatut, setFilterStatut] = useState('all');
  
  // Center on Drôme/Vaucluse (zone lavande)
  const [mapCenter] = useState([44.25, 5.15]);
  const [mapZoom] = useState(10);
  
  // Formulaire nouvelle benne
  const [newBenne, setNewBenne] = useState({
    code: '',
    type_benne: 'caisson',
    capacite_kg: 3000
  });
  
  // Formulaire signaler pleine
  const [poidsEstime, setPoidsEstime] = useState('');
  
  const loadData = useCallback(async () => {
    try {
      const [bennesRes, alertesRes, planningRes] = await Promise.all([
        api.get('/distillateur/bennes/carte').catch(() => ({ data: { bennes: [] } })),
        api.get('/distillateur/alertes?non_traitees=true').catch(() => ({ data: { alertes: [] } })),
        api.get('/distillateur/planning/bennes').catch(() => ({ data: null }))
      ]);
      
      setBennes(bennesRes.data.bennes || []);
      setAlertes(alertesRes.data.alertes || []);
      setPlanning(planningRes.data);
      
      // Calculer stats
      const statsCalc = {};
      (bennesRes.data.bennes || []).forEach(b => {
        statsCalc[b.statut] = (statsCalc[b.statut] || 0) + 1;
      });
      setStats(statsCalc);
      
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);
  
  // Filtrer les bennes
  const filteredBennes = filterStatut === 'all' 
    ? bennes 
    : bennes.filter(b => b.statut === filterStatut);
  
  // Créer une nouvelle benne
  const handleCreateBenne = async () => {
    if (!newBenne.code) {
      toast.error('Le code de la benne est requis');
      return;
    }
    
    try {
      await api.post('/distillateur/bennes', newBenne);
      toast.success(`Benne ${newBenne.code} créée`);
      setShowCreateDialog(false);
      setNewBenne({ code: '', type_benne: 'caisson', capacite_kg: 3000 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };
  
  // Signaler benne pleine
  const handleSignalerPleine = async () => {
    if (!selectedBenne) return;
    
    try {
      await api.post(`/distillateur/bennes/${selectedBenne.id}/signaler-pleine`, null, {
        params: { poids_estime_kg: poidsEstime ? parseFloat(poidsEstime) : null }
      });
      toast.success(`Benne ${selectedBenne.code} signalée comme pleine - Alerte envoyée`);
      setShowSignalerPleineDialog(false);
      setPoidsEstime('');
      setSelectedBenne(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };
  
  // Traiter une alerte
  const handleTraiterAlerte = async (alerteId) => {
    try {
      await api.patch(`/distillateur/alertes/${alerteId}/traiter`);
      toast.success('Alerte traitée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };
  
  // Changer le statut d'une benne
  const handleChangeStatut = async (benneId, nouveauStatut) => {
    try {
      await api.patch(`/distillateur/bennes/${benneId}`, { statut: nouveauStatut });
      toast.success('Statut mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-200px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bennes-distillerie-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Package className="w-8 h-8 text-orange-600" />
            Bennes & Caissons
          </h1>
          <p className="text-muted-foreground">
            Suivi en temps réel des bennes sur les parcelles et à la distillerie
          </p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle Benne
          </Button>
          
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(BENNE_STYLES).map(([key, style]) => (
                <SelectItem key={key} value={key}>
                  {style.icon} {style.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card className="bg-green-50 dark:bg-green-900/20">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-green-600">{stats.pleine || 0}</div>
            <div className="text-xs text-muted-foreground">Pleines à récupérer</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-blue-600">{stats.posee_parcelle || 0}</div>
            <div className="text-xs text-muted-foreground">Sur parcelles</div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-900/20">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-purple-600">{stats.en_transport || 0}</div>
            <div className="text-xs text-muted-foreground">En transport</div>
          </CardContent>
        </Card>
        <Card className="bg-cyan-50 dark:bg-cyan-900/20">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-cyan-600">{stats.en_attente_distillation || 0}</div>
            <div className="text-xs text-muted-foreground">En attente distil.</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-3">
            <div className="text-2xl font-bold text-red-600">{stats.en_distillation || 0}</div>
            <div className="text-xs text-muted-foreground">En distillation</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 dark:bg-gray-900/20">
          <CardContent className="p-3">
            <div className="text-2xl font-bold">{stats.disponible || 0}</div>
            <div className="text-xs text-muted-foreground">Disponibles</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Carte et Planning */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Carte */}
        <div className="lg:col-span-2 relative h-[500px] rounded-xl overflow-hidden border">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            className="h-full w-full"
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; ESRI'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            />
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
            />
            
            {/* Bennes sur la carte */}
            {filteredBennes.map(benne => {
              if (!benne.position) return null;
              
              return (
                <Marker
                  key={benne.id}
                  position={[benne.position.lat, benne.position.lng]}
                  icon={createBenneIcon(benne)}
                  eventHandlers={{
                    click: () => setSelectedBenne(benne)
                  }}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg">{benne.code}</span>
                        <Badge 
                          style={{ backgroundColor: BENNE_STYLES[benne.statut]?.color }}
                          className="text-white"
                        >
                          {BENNE_STYLES[benne.statut]?.label}
                        </Badge>
                      </div>
                      
                      {benne.est_bio && (
                        <Badge variant="outline" className="mb-2 bg-green-50 text-green-700 border-green-300">
                          <Leaf className="w-3 h-3 mr-1" /> BIO
                        </Badge>
                      )}
                      
                      <div className="space-y-1 text-sm">
                        {benne.parcelle_nom && (
                          <p><MapPin className="w-3 h-3 inline mr-1" /> {benne.parcelle_nom}</p>
                        )}
                        {benne.producteur_nom && (
                          <p>Producteur: {benne.producteur_nom}</p>
                        )}
                        {benne.plante && (
                          <p>Plante: {benne.plante}</p>
                        )}
                        {benne.poids_kg && (
                          <p><Scale className="w-3 h-3 inline mr-1" /> {benne.poids_kg} kg</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-3">
                        {benne.statut === 'posee_parcelle' && (
                          <Button 
                            size="sm" 
                            className="w-full bg-green-600 hover:bg-green-700"
                            onClick={() => {
                              setSelectedBenne(benne);
                              setShowSignalerPleineDialog(true);
                            }}
                          >
                            Signaler pleine
                          </Button>
                        )}
                        {benne.statut === 'pleine' && (
                          <Button 
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={() => handleChangeStatut(benne.id, 'en_transport')}
                          >
                            <Truck className="w-3 h-3 mr-1" /> En transport
                          </Button>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
          
          {/* Légende */}
          <BenneLegend stats={stats} />
          
          {/* Alertes */}
          <AlertesPanel 
            alertes={alertes} 
            onTraiter={handleTraiterAlerte}
            onRefresh={loadData}
          />
        </div>
        
        {/* Planning / File d'attente */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Planning Distillation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {planning ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="p-2 bg-muted/50 rounded">
                      <div className="font-bold text-lg">{planning.bennes_en_attente}</div>
                      <div className="text-xs text-muted-foreground">En attente</div>
                    </div>
                    <div className="p-2 bg-muted/50 rounded">
                      <div className="font-bold text-lg">{planning.capacite_restante_24h}</div>
                      <div className="text-xs text-muted-foreground">Créneaux dispo</div>
                    </div>
                  </div>
                  
                  {planning.heure_fin_estimee && (
                    <div className="text-sm text-center p-2 bg-orange-50 dark:bg-orange-900/20 rounded">
                      Fin estimée: <span className="font-bold">{planning.heure_fin_estimee}</span>
                    </div>
                  )}
                  
                  <ScrollArea className="h-[280px]">
                    <div className="space-y-2">
                      {(planning.creneaux_prevus || []).map((creneau, idx) => (
                        <div 
                          key={idx} 
                          className={`p-2 rounded-lg border ${
                            creneau.necessite_affranchissement 
                              ? 'bg-green-50 dark:bg-green-900/20 border-green-200' 
                              : 'bg-muted/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-bold">{creneau.benne_code}</span>
                            <span className="text-xs text-muted-foreground">
                              {creneau.heure_estimee_debut} - {creneau.heure_estimee_fin}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {creneau.plante} • {creneau.producteur}
                            {creneau.poids_kg && ` • ${creneau.poids_kg}kg`}
                          </div>
                          {creneau.necessite_affranchissement && (
                            <Badge variant="outline" className="mt-1 text-[10px] bg-green-100 text-green-700">
                              Affranchissement vapeur requis
                            </Badge>
                          )}
                        </div>
                      ))}
                      
                      {(!planning.creneaux_prevus || planning.creneaux_prevus.length === 0) && (
                        <div className="text-center py-8 text-muted-foreground text-sm">
                          Aucune benne en file d'attente
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Chargement du planning...
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Liste des bennes */}
      <Card>
        <CardHeader>
          <CardTitle>Toutes les bennes ({bennes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {filteredBennes.map(benne => {
              const style = BENNE_STYLES[benne.statut] || BENNE_STYLES.disponible;
              return (
                <div 
                  key={benne.id}
                  className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                    benne.est_bio ? 'ring-2 ring-green-500' : ''
                  }`}
                  onClick={() => setSelectedBenne(benne)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded flex items-center justify-center text-lg"
                        style={{ backgroundColor: style.color }}
                      >
                        {style.icon}
                      </div>
                      <span className="font-bold">{benne.code}</span>
                    </div>
                    {benne.est_bio && (
                      <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700">BIO</Badge>
                    )}
                  </div>
                  <Badge style={{ backgroundColor: style.color }} className="text-white text-xs">
                    {style.label}
                  </Badge>
                  {benne.parcelle_nom && (
                    <p className="text-xs text-muted-foreground mt-2 truncate">
                      <MapPin className="w-3 h-3 inline" /> {benne.parcelle_nom}
                    </p>
                  )}
                  {benne.plante && (
                    <p className="text-xs text-muted-foreground truncate">
                      {benne.plante}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
      
      {/* Dialog création benne */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle Benne</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code de la benne *</Label>
              <Input
                placeholder="Ex: DI12, DI8, BT01..."
                value={newBenne.code}
                onChange={(e) => setNewBenne({ ...newBenne, code: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={newBenne.type_benne} 
                onValueChange={(v) => setNewBenne({ ...newBenne, type_benne: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="caisson">Caisson distillation</SelectItem>
                  <SelectItem value="benne_tp">Benne TP (transport)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Capacité (kg)</Label>
              <Input
                type="number"
                value={newBenne.capacite_kg}
                onChange={(e) => setNewBenne({ ...newBenne, capacite_kg: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateBenne}>
              <Plus className="w-4 h-4 mr-2" /> Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog signaler pleine */}
      <Dialog open={showSignalerPleineDialog} onOpenChange={setShowSignalerPleineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signaler benne pleine</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-bold">{selectedBenne?.code}</p>
              <p className="text-sm text-muted-foreground">{selectedBenne?.parcelle_nom}</p>
            </div>
            <div className="space-y-2">
              <Label>Poids estimé (kg) - optionnel</Label>
              <Input
                type="number"
                placeholder="Ex: 2500"
                value={poidsEstime}
                onChange={(e) => setPoidsEstime(e.target.value)}
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Une alerte sera envoyée aux chauffeurs pour récupérer cette benne.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignalerPleineDialog(false)}>Annuler</Button>
            <Button onClick={handleSignalerPleine} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="w-4 h-4 mr-2" /> Signaler pleine
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
